import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus } from "@fortawesome/free-solid-svg-icons";
import React, { useEffect, useMemo, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { createEditorState, shortUUID } from "../helpers";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import BroadcastEditorMessagePane from "./BroadcastEditorMessagePane";
import { proxyAPI } from "../proxyAPI";
import SendIcon from "../../assets/icons/send.svg2";
import { useSelector } from "react-redux";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import { toast } from "react-toastify";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import BroadcastSendModals from "./BroadcastSendModals";
import { selectimportedFile } from "../store/slices/broadcastSlice";
import {
  BroadcastChannelType,
  BroadcastType,
} from "../store/slices/broadcastSlice";
import { useGetCommsSettingsDataQuery } from "../store/slices/settingsSlice";
import ContactsList from "../Contacts/ContactsList";
import { useListContactsAndGroupsInLocationQuery } from "../store/slices/mapsSlice";
import { toCamel } from "../Maps/helpers";
import MyPagination from "../Common/MyPagination";
import { EventContacts } from "../Events/EventsContactsSlider";
import Contacts from "../Contacts/Contacts";
import DynamicHeaderInput from "../Builder/nodes/Logic/Inputs/DynamicHeaderInput";
import InputLabel from "../Builder/nodes/Logic/Inputs/InputLabel";

const emptyArray: unknown = [];
const createEventRecipientFilters = (eventUuid: string) => [
  {
    fields: ["event"],
    formats: [],
    isOr: true,
    offset: "0",
    offsetFrom: "fixed",
    offsetType: "positive",
    offsetUnit: "day",
    operator: "is",
    order: 0,
    thread: "CurrentThread",
    timeZone: "",
    timeZoneFrom: "fixed",
    valueId: "",
    values: [eventUuid],
    valueType: "fixed",
  },
];

export default function LightBroadcast({
  showSendButton = true,
  showSendTestButton = false,
  tokenTags,
  showRecipientsSelection = true,
  showRecipients = true,
  showEventRecipients = false,
  showAssetSelection = true,
  showAssetSelectionOnTop = false,
  broadcastState,
  setBroadcastState,
  initialSendList,
  setSendList,
  events,
}: {
  showSendButton?: boolean;
  showSendTestButton?: boolean;
  tokenTags?: string[];
  showRecipientsSelection?: boolean;
  showRecipients?: boolean;
  showEventRecipients?: boolean;
  showAssetSelectionOnTop?: boolean;
  showAssetSelection?: boolean;
  broadcastState?: BroadcastType;
  setBroadcastState?: (broadcast: BroadcastType) => void;
  initialSendList?: {
    contacts: string[];
    ignoredContacts: string[];
    contactSets: string[];
    ignoredContactSets: string[];
    assets: string[];
    clearStatic: boolean;
  };
  setSendList?: (sendList: typeof initialSendList) => void;
  events?: string[];
}) {
  const { data: commsSettings } = useGetCommsSettingsDataQuery();

  const createInitialBroadcastData = (): BroadcastType => ({
    FromUuid: "",
    ClientID: 1,
    Status: "draft",
    Label: "New broadcast - " + new Date().toLocaleString(),
    Description: "New broadcast",
    Reference: shortUUID().replace(/-/g, "").slice(0, 8),
    Email: "developers@boomcomms.com",
    Messages: [
      {
        Channel: "sms",
        Uuid: shortUUID(),
        DynamicHeader: commsSettings?.SmsHeader || "",
        EditDefaults: false,
        Subject: "",
        PlainText: "",
        Tokens: [],
        Sender: "",
        RetryAttempts: 1,
        RetryDelay: 5,
        RetryUnit: "minutes",
        IsRedirect: false,
        RedirectMessage: "",
        RedirectNumber: "",
        SelectComms: "first",
        SelectCommsUuids: [],
        Message: {
          Plain: "",
          PlainText: "",
          Rendered: "",
          EditorState: createEditorState(),
        },
        Shortlinks: {
          uuid: "",
          startingWith: [],
          expiresAfterVisits: 0,
          expireAfterDays: 0,
          expires: "",
          isProtected: false,
          password: "",
        },
      },
    ],
    CommsMethod: "one-way",
    ContactUuids: [],
    Groups: [],
    SendList: "",
    IsFileImport: false,
    Handlers: [],
    TagsMap: { category: [], industry: [], folder: [] },
    Events: events || [],
  });

  const broadcastUUID = "new";
  const [sendAction, setSendAction] = useState<
    "send-broadcast" | "send-test" | undefined
  >();

  const [localBroadcastData, setLocalBroadcastData] = useState<BroadcastType>(
    () => createInitialBroadcastData()
  );
  const broadcastData = broadcastState || localBroadcastData;
  const persistBroadcastData = setBroadcastState || setLocalBroadcastData;

  const [loading, setLoading] = useState(false);

  // send list uuid state
  // default to broadcast-sendlist before it is saved
  // if file import is selected it will be set to broadcast-import-file
  const [sendListUUID, setSendListUUID] = useState("broadcast-sendlist");
  const importedFile = useSelector(selectimportedFile);
  const [selectedContacts, setSelectedContacts] = useState<any[]>(
    initialSendList?.contacts || []
  );
  const [selectedContactSets, setSelectedContactSets] = useState<any[]>(
    initialSendList?.contactSets || []
  );
  const [selectedAssets, setSelectedAssets] = useState<any[]>(
    initialSendList?.assets || []
  );
  const [ignoredContacts, setIgnoredContacts] = useState<any[]>(
    initialSendList?.ignoredContacts || []
  );
  const [ignoredContactSets, setIgnoredContactSets] = useState<any[]>(
    initialSendList?.ignoredContactSets || []
  );

  const [isBroadcastFixed, setIsBroadcastFixed] = useState(false);
  useEffect(() => {
    if (commsSettings) {
      setIsBroadcastFixed(commsSettings.IsBroadcastFixed);
    }
  }, [commsSettings]);

  const sendList = useMemo(
    () => ({
      contacts: selectedContacts,
      ignoredContacts,
      contactSets: selectedContactSets,
      ignoredContactSets,
      assets: selectedAssets,
      clearStatic: false,
    }),
    [
      selectedContacts,
      ignoredContacts,
      selectedContactSets,
      ignoredContactSets,
      selectedAssets,
    ]
  );

  useEffect(() => {
    if (setSendList) {
      setSendList(sendList);
    }
  }, [
    selectedContacts,
    selectedContactSets,
    selectedAssets,
    ignoredContacts,
    ignoredContactSets,
    setSendList,
  ]);

  const setBroadcastData = (v: BroadcastType) => {
    const nextValues = { ...v };
    persistBroadcastData(nextValues);
  };

  const send = async (testNumber: string | null) => {
    setLoading(true);

    // get imported contact fields
    const FileImportHeader = [
      ...new Set(
        broadcastData?.BuilderConfiguration?.Tokens.filter(
          (t: any) => t.Tag === "imported_contact_field"
        ).map((t: any) => t.Value)
      ),
    ];

    const body = {
      ...broadcastData,

      ContactUuids: sendList.contacts,
      IgnoreContactUuids: sendList.ignoredContacts,
      Groups: sendList.contactSets,
      IgnoreGroups: sendList.ignoredContactSets,
      Assets: sendList.assets,
      // if remove all is clicked
      SendList: sendList.clearStatic ? "" : sendListUUID,

      ...(sendListUUID === "broadcast-import-file"
        ? {
            File: importedFile?.file,
            IsFileImport: FileImportHeader.length > 0,
            FileImportHeader,
          }
        : {}),
      // Ignore everything if test send
      ...(testNumber
        ? {
            ContactUuids: [testNumber],
            Groups: [],
            SendList: "",
            File: "",
            IsFileImport: false,
          }
        : {}),
    };

    const data = await proxyAPI({
      prefix: "logic/workflows/api",
      url: "broadcasters/send",
      method: "POST",
      body,
    });
    setTimeout(() => {
      setLoading(false);
      if (data) {
        toast.success(data.message);
      }
    }, 200);
    return data;
  };

  const [showErrors, setShowErrors] = useState<"test" | boolean>(false);
  const handleCloseSendModals = () => {
    setSendAction(undefined);
    setShowErrors(false);
  };

  const handleShowSendErrors = ((sendType: "test" | undefined) => {
    if (
      errors.filter((e) =>
        sendType === "test" ? e.type !== "Message recipients" : true
      ).length > 0
    ) {
      toast.error("Please fix errors before sending.");
      setShowErrors(sendType || true);
    } else {
      setShowErrors(false);
    }
  }) as any;
  // checking errors
  const errors: {
    type: "Message recipients" | "Message section";
    content: JSX.Element | string;
    channel?: string;
  }[] = [];
  // no-contacts
  if (
    (sendList.contacts?.length ?? 0) === 0 &&
    (sendList.contactSets?.length ?? 0) === 0 &&
    !broadcastData?.SendList &&
    !broadcastData?.Events?.length &&
    !importedFile
  ) {
    errors.push({
      type: "Message recipients",
      content: <span>No contacts selected. </span>,
    });
  }

  broadcastData?.Messages?.filter(
    (m) => m.Message.EditorState === null || m.Message.PlainText === ""
  ).forEach((m) =>
    errors.push({
      type: "Message section",
      channel: m.Channel,
      content: "Message content is missing for " + m.Channel,
    })
  );

  if (
    broadcastData?.Messages?.find((m) => m.Channel === "email")?.EmailSubject
      ?.PlainText === ""
  ) {
    errors.push({
      type: "Message section",
      channel: "email",
      content: "Empty email subject",
    });
  }

  if (
    broadcastData?.Handlers?.find(
      (h) =>
        h.Do === "forward" &&
        h.Message?.EditorState?.blocks?.map((b: any) => b.text).join("") === ""
    )
  ) {
    errors.push({
      type: "Message section",
      content: "Handler with forward action has empty message content",
    });
  }

  if (
    broadcastData?.Handlers?.find(
      (h) =>
        h.Do === "forward" &&
        h.ForwardMethod === "email" &&
        h.EmailSubject?.EditorState?.blocks
          ?.map((b: any) => b.text)
          .join("") === ""
    )
  ) {
    errors.push({
      type: "Message section",
      content: "Handler with forward action has empty email subject field",
    });
  }

  if (
    broadcastData?.Handlers?.find(
      (h) =>
        h.Do === "forward" &&
        h.ForwardMethod === "email" &&
        h.EmailFrom?.EditorState?.blocks?.map((b: any) => b.text).join("") ===
          ""
    )
  ) {
    errors.push({
      type: "Message section",
      content: "Handler with forward action has empty email from field",
    });
  }

  const visibleErrors = errors;

  const channelOptions = [
    { label: "SMS", value: "sms" },
    { label: "EMAIL", value: "email" },
    { label: "VOICE", value: "voice" },
  ];

  const withChannelDefaults = (message: any, channel: BroadcastChannelType) => {
    const nextMessage = {
      ...message,
      Channel: channel,
    };

    if (channel === "voice") {
      return {
        ...nextMessage,
        WelcomeMessage:
          nextMessage.WelcomeMessage ?? commsSettings?.VoiceIntro ?? "",
        ExitMessage: nextMessage.ExitMessage ?? commsSettings?.VoiceExit ?? "",
        RetryAttempts:
          nextMessage.RetryAttempts ?? commsSettings?.VoiceRetries ?? 1,
      };
    }

    if (channel === "email") {
      return {
        ...nextMessage,
        EmailFrom: nextMessage.EmailFrom || {
          EditorState: createEditorState(commsSettings?.EmailFrom || ""),
        },
        EmailSubject: nextMessage.EmailSubject || {
          EditorState: createEditorState(commsSettings?.EmailSubject || ""),
        },
        EmailSignature: nextMessage.EmailSignature || {
          EditorState: createEditorState(commsSettings?.EmailSignature || ""),
        },
      };
    }

    return nextMessage;
  };

  const handleMessageChange = (nextMessage: any, meta: any = {}) => {
    const nextValues: any = {
      ...broadcastData,
      Messages: [nextMessage],
    };

    if (meta.usedIn) {
      nextValues.BuilderConfiguration = {
        ...(broadcastData?.BuilderConfiguration || {}),
        Tokens: [
          ...(broadcastData?.BuilderConfiguration?.Tokens || []).filter(
            (t: any) =>
              t.UsedIn !== meta.usedIn || t.Channel !== nextMessage.Channel
          ),
          ...(meta.tokens || []).map((t: any) => ({
            ...t,
            UsedIn: meta.usedIn,
            Channel: nextMessage.Channel,
          })),
        ],
      };
    }

    setBroadcastData(nextValues);
  };

  // Always use the first message in the array
  // Always use the first message in the array, and force a fresh reference after updates
  const firstMessageIndex = 0;
  const firstMessage = React.useMemo(
    () => broadcastData?.Messages?.[0],
    [broadcastData?.Messages]
  );

  const [recipientsPagination, setRecipientsPagination] = useState({
    page: 1,
    perPage: 12,
  });
  const eventRecipientFilters = useMemo(
    () =>
      (broadcastData.Events || []).map((eventUuid) => ({
        eventUuid,
        filters: [
          ...createEventRecipientFilters(eventUuid),
          ...((selectedContacts?.length || 0) > 0
            ? [
                {
                  fields: ["uuid"],
                  formats: [],
                  isOr: true,
                  offset: "0",
                  offsetFrom: "fixed",
                  offsetType: "positive",
                  offsetUnit: "day",
                  operator: "is",
                  order: 0,
                  thread: "CurrentThread",
                  timeZone: "",
                  timeZoneFrom: "fixed",
                  valueId: "",
                  values: selectedContacts,
                  valueType: "fixed",
                },
              ]
            : []),
          ...((selectedContactSets?.length || 0) > 0
            ? [
                {
                  fields: ["group"],
                  formats: [],
                  isOr: true,
                  offset: "0",
                  offsetFrom: "fixed",
                  offsetType: "positive",
                  offsetUnit: "day",
                  operator: "is",
                  order: 0,
                  thread: "CurrentThread",
                  timeZone: "",
                  timeZoneFrom: "fixed",
                  valueId: "",
                  values: selectedContactSets,
                  valueType: "fixed",
                },
              ]
            : []),
        ],
      })),
    [broadcastData.Events, selectedContacts, selectedContactSets]
  );
  const { data: recipientsData } = useListContactsAndGroupsInLocationQuery(
    {
      IncludeContacts: selectedContacts,
      IncludeGroups: selectedContactSets,
      IncludeAssets: selectedAssets,
      ExcludeContacts: ignoredContacts,
      ExcludeGroups: ignoredContactSets,
      Page: recipientsPagination.page,
      PerPage: recipientsPagination.perPage,
    },
    { skip: !selectedContacts && !selectedContactSets && !selectedAssets }
  );

  return (
    broadcastData && (
      <>
        <FullScreenLoadingIndicator loading={loading} />

        {showErrors &&
          errors.filter((e) =>
            showErrors === "test" ? e.type !== "Message recipients" : true
          ).length > 0 && (
            <div className="alert alert-danger">
              <ul className="mb-0">
                {errors
                  .filter((e) =>
                    showErrors === "test"
                      ? e.type !== "Message recipients"
                      : true
                  )
                  .map((e, i) => (
                    <li key={i}>{e.content}</li>
                  ))}
              </ul>
            </div>
          )}

        <div className="">
          {showAssetSelectionOnTop && (
            <div className="mb-3">
              <Input
                inputType="asset"
                label="Assets"
                value={selectedAssets}
                onChange={setSelectedAssets}
                isMultiple
              />
            </div>
          )}
          <div className="mb-3 d-flex align-items-end justify-content-between">
            <Input
              inputType="radio"
              label="Channel"
              value={[firstMessage?.Channel]}
              onChange={(v) => {
                if (v?.[0] && firstMessage) {
                  handleMessageChange(
                    withChannelDefaults(
                      firstMessage,
                      v[0] as BroadcastChannelType
                    )
                  );
                }
              }}
              options={channelOptions}
              radioName="lightbroadcast-channel-selector"
            />
            {firstMessage.Channel === "sms" && (
              <div style={{ minWidth: 300 }}>
                <InputLabel>Dynamic header</InputLabel>
                <DynamicHeaderInput
                  placeholder="Message config DynamicHeader"
                  value={firstMessage?.DynamicHeader ?? ""}
                  onChange={(v) =>
                    handleMessageChange(
                      { ...firstMessage, DynamicHeader: v },
                      {}
                    )
                  }
                />
              </div>
            )}
          </div>
          {firstMessage && (
            <BroadcastEditorMessagePane
              tokenTags={tokenTags}
              message={firstMessage}
              onChange={(nextMessage: any, meta: any) =>
                handleMessageChange(nextMessage, meta)
              }
              isFileImport={broadcastData.IsFileImport}
              SignatureModal={() => null}
            />
          )}
          {showRecipientsSelection && (
            <div className="">
              <div className="mb-3">
                <Input
                  inputType="contact"
                  label="Contacts"
                  value={selectedContacts}
                  onChange={setSelectedContacts}
                  isMultiple
                />
              </div>
              <div className="mb-3">
                <Input
                  inputType="contactSet"
                  label="Groups"
                  value={selectedContactSets}
                  onChange={setSelectedContactSets}
                  isMultiple
                />
              </div>
              {showAssetSelection && (
                <div className="mb-3">
                  <Input
                    inputType="asset"
                    label="Assets"
                    value={selectedAssets}
                    onChange={setSelectedAssets}
                    isMultiple
                  />
                </div>
              )}
            </div>
          )}
          {/* Selected recipients */}
          <>
            <div className="fw-bold fs-4">Recipients</div>
            {ignoredContacts?.length > 0 ? (
              <Tabs>
                {showRecipients && (
                  <Tab
                    eventKey="selected"
                    title={`Selected (${recipientsData?.total})`}
                  >
                    <div className="mb-3">
                      {selectedContacts?.length ||
                      selectedContactSets?.length ||
                      selectedAssets?.length ? (
                        <div className="">
                          <ContactsList
                            contacts={toCamel(recipientsData?.contacts) || []}
                            allContactsButton={undefined}
                            refreshList={undefined}
                            notDraggable={undefined}
                            fullView={undefined}
                            isModal={undefined}
                            showContactFields={undefined}
                            onSelectContacts={undefined}
                            contactButton={(c: any) => {
                              if (
                                (c.contactSets || []).some((cs: any) =>
                                  selectedContactSets.includes(cs.uuid)
                                )
                              ) {
                                return (
                                  <span>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger border-0 btn-sm me-2"
                                      title="Ignore this contact"
                                      onClick={() => {
                                        setIgnoredContacts([
                                          ...ignoredContacts,
                                          c.uuid,
                                        ]);
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faMinus} />
                                    </button>
                                    <span
                                      className="bg-secondary text-white rounded-2 px-2 py-1"
                                      title="This contact was selected as part of a group. You need to deselect the group to remove this contact."
                                    >
                                      Group
                                    </span>
                                  </span>
                                );
                              }
                            }}
                          />
                          <MyPagination
                            total={recipientsData?.total ?? 0}
                            onPaginate={(page, perPage) =>
                              setRecipientsPagination({ page, perPage })
                            }
                          />
                        </div>
                      ) : (
                        <div className="alert alert-secondary">
                          No criteria selected.
                        </div>
                      )}
                    </div>
                  </Tab>
                )}
                {showEventRecipients && !!broadcastData.Events?.length && (
                  <Tab
                    eventKey={"selected"}
                    title="Contacts Associated to Event"
                  >
                    {eventRecipientFilters.map(({ eventUuid, filters }) => (
                      <Contacts
                        key={eventUuid}
                        filters={filters}
                        fields={undefined}
                        useSearchOptions={true}
                        includeContacts={undefined}
                        ignoreContacts={ignoredContacts}
                        ignoreSets={undefined}
                        contactSet={undefined}
                        contactSets={emptyArray}
                        strictCriteria={undefined}
                        doNotUseSets={undefined}
                        contactButton={(c: any) => (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline-danger border-0 btn-sm me-2"
                              title="Ignore this contact"
                              onClick={() => {
                                setIgnoredContacts([
                                  ...ignoredContacts,
                                  c.uuid,
                                ]);
                              }}
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </button>
                          </>
                        )}
                        notDraggable={undefined}
                        onDrop={undefined}
                        allContactsButton={undefined}
                        isModal={undefined}
                        showContactFields={undefined}
                        setSelectedContacts={undefined}
                      />
                    ))}
                  </Tab>
                )}

                <Tab
                  eventKey="ignored"
                  title={`Ignored (${ignoredContacts.length})`}
                >
                  <div className="mb-3">
                    <div className="">
                      <Contacts
                        filters={undefined}
                        fields={undefined}
                        useSearchOptions={undefined}
                        includeContacts={ignoredContacts}
                        ignoreContacts={undefined}
                        ignoreSets={undefined}
                        contactSet={undefined}
                        contactSets={undefined}
                        strictCriteria={undefined}
                        doNotUseSets={undefined}
                        notDraggable={undefined}
                        onDrop={undefined}
                        isModal={undefined}
                        showContactFields={undefined}
                        setSelectedContacts={undefined}
                        allContactsButton={(c: any) => (
                          <button
                            type="button"
                            className="btn btn-outline-danger border-0 btn-sm me-2"
                            title="Ignore this contact"
                            onClick={() => {
                              setIgnoredContacts([]);
                            }}
                          >
                            <FontAwesomeIcon icon={faMinus} />
                          </button>
                        )}
                        contactButton={(c: any) => (
                          <button
                            type="button"
                            className="btn btn-outline-danger border-0 btn-sm me-2"
                            title="Ignore this contact"
                            onClick={() => {
                              setIgnoredContacts(
                                ignoredContacts.filter(
                                  (uuid) => uuid !== c.uuid
                                )
                              );
                            }}
                          >
                            <FontAwesomeIcon icon={faMinus} />
                          </button>
                        )}
                      />
                      <MyPagination
                        total={recipientsData?.total ?? 0}
                        onPaginate={(page, perPage) =>
                          setRecipientsPagination({ page, perPage })
                        }
                      />
                    </div>
                  </div>
                </Tab>
              </Tabs>
            ) : (
              <div className="mb-3">
                {showRecipients && (
                  <div className="mb-3">
                    <div className="fw-bold mb-2">
                      Selected ({recipientsData?.total})
                    </div>
                    {selectedContacts?.length ||
                    selectedContactSets?.length ||
                    selectedAssets?.length ? (
                      <div className="">
                        <ContactsList
                          contacts={toCamel(recipientsData?.contacts) || []}
                          allContactsButton={undefined}
                          refreshList={undefined}
                          notDraggable={undefined}
                          fullView={undefined}
                          isModal={undefined}
                          showContactFields={undefined}
                          onSelectContacts={undefined}
                          contactButton={(c: any) => {
                            if (
                              (c.contactSets || []).some((cs: any) =>
                                selectedContactSets.includes(cs.uuid)
                              )
                            ) {
                              return (
                                <span>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger border-0 btn-sm me-2"
                                    title="Ignore this contact"
                                    onClick={() => {
                                      setIgnoredContacts([
                                        ...ignoredContacts,
                                        c.uuid,
                                      ]);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faMinus} />
                                  </button>
                                  <span
                                    className="bg-secondary text-white rounded-2 px-2 py-1"
                                    title="This contact was selected as part of a group. You need to deselect the group to remove this contact."
                                  >
                                    Group
                                  </span>
                                </span>
                              );
                            }
                          }}
                        />
                        <MyPagination
                          total={recipientsData?.total ?? 0}
                          onPaginate={(page, perPage) =>
                            setRecipientsPagination({ page, perPage })
                          }
                        />
                      </div>
                    ) : (
                      <div className="alert alert-secondary">
                        No criteria selected.
                      </div>
                    )}
                  </div>
                )}
                {showEventRecipients && !!broadcastData.Events?.length && (
                  <div className="mb-3">
                    <div className="fw-bold mb-2">
                      Contacts Associated to Event
                    </div>
                    {eventRecipientFilters.map(({ eventUuid, filters }) => (
                      <Contacts
                        key={eventUuid}
                        filters={filters}
                        fields={undefined}
                        useSearchOptions={true}
                        includeContacts={undefined}
                        ignoreContacts={ignoredContacts}
                        ignoreSets={undefined}
                        contactSet={undefined}
                        contactSets={emptyArray}
                        strictCriteria={undefined}
                        doNotUseSets={undefined}
                        contactButton={(c: any) => (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline-danger border-0 btn-sm me-2"
                              title="Ignore this contact"
                              onClick={() => {
                                setIgnoredContacts([
                                  ...ignoredContacts,
                                  c.uuid,
                                ]);
                              }}
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </button>
                          </>
                        )}
                        notDraggable={undefined}
                        onDrop={undefined}
                        allContactsButton={undefined}
                        isModal={undefined}
                        showContactFields={undefined}
                        setSelectedContacts={undefined}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>

          <div className="mx-auto my-3 d-flex justify-content-center">
            {showSendTestButton && (
              <button
                type="button"
                className="fw-bold text-white px-3 py-1  me-2 btn small border-dark rounded-pill  border-2 text-nowrap text-white bg-colored"
                onClick={() => {
                  if (visibleErrors.length > 0) {
                    handleShowSendErrors(undefined);
                    setSendAction(undefined);
                    return;
                  }
                  setSendAction("send-test");
                }}
              >
                <SendIcon width={20} className="white" />
                <span className="p-2">TEST</span>
              </button>
            )}
            {showSendButton && (
              <button
                type="button"
                className="fw-bold text-white px-3 py-1  me-2 btn small border-dark rounded-pill  border-2 text-nowrap text-white bg-colored"
                onClick={() => {
                  if (visibleErrors.length > 0) {
                    handleShowSendErrors(undefined);
                    setSendAction(undefined);
                    return;
                  }
                  setSendAction("send-broadcast");
                }}
              >
                <SendIcon width={20} className="white" />
                <span className="p-2">SEND</span>
              </button>
            )}
          </div>
        </div>

        {visibleErrors.length === 0 && (
          <BroadcastSendModals
            action={sendAction}
            isTest={sendAction === "send-test"}
            values={broadcastData}
            recipientTotal={recipientsData?.total}
            send={send}
            sendList={sendList}
            broadcastUUID={broadcastUUID}
            onHide={handleCloseSendModals}
          />
        )}
        <JsonCollapse json={broadcastData} />
      </>
    )
  );
}
